import { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { Check, Receipt, Loader2, CheckCircle2 } from "lucide-react";

const fmt = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function QuoteBuilder() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const editId = params.get("edit");
  const [catalog, setCatalog] = useState({ packages: [], addons: [] });
  const [pkg, setPkg] = useState(null);
  const [addons, setAddons] = useState({});
  const [form, setForm] = useState({ client_name: "", client_email: "", company: "", needs: "", website_url: "", website: "", password: "" });
  const prefilledRef = useRef(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [hasWebsite, setHasWebsite] = useState(false);
  const [fromAudit, setFromAudit] = useState(false);

  useEffect(() => {
    api.get("/catalog").then(({ data }) => setCatalog(data)).catch(() => {});
  }, []);

  // Load existing quote when editing
  useEffect(() => {
    if (!editId) return;
    api.get(`/quotes/${editId}`).then(({ data }) => {
      setPkg(data.package_id || null);
      const a = {};
      (data.addon_ids || []).forEach((id) => { a[id] = true; });
      setAddons(a);
      setForm({ client_name: data.client_name || "", client_email: data.client_email || "", company: data.company || "", needs: data.needs || "", website_url: data.website_url || "", website: "" });
      setHasWebsite(!!data.website_url);
    }).catch(() => toast.error("Could not load quote to edit"));
  }, [editId]);

  // Apply recommended items prefill (from website audit upsell)
  useEffect(() => {
    if (editId) return;
    const raw = sessionStorage.getItem("builderPrefill");
    if (!raw) return;
    prefilledRef.current = true;
    try {
      const p = JSON.parse(raw);
      setPkg(p.pkg || null);
      setAddons(p.addons || {});
      setForm((f) => ({ ...f, ...(p.form || {}) }));
      if (p.form?.website_url) setHasWebsite(true);
      setFromAudit(!!p.from_audit);
      toast.success(p.from_audit ? "Loaded recommended items — review and create the upsell quote" : "Client details pre-filled — build their quote");
    } catch {}
    sessionStorage.removeItem("builderPrefill");
  }, [editId]);

  // Restore pending selection after login redirect (skip when editing)
  useEffect(() => {
    if (editId) return;
    if (prefilledRef.current) return;
    const pending = sessionStorage.getItem("pendingQuote");
    if (pending) {
      prefilledRef.current = true;
      try {
        const p = JSON.parse(pending);
        setPkg(p.pkg || null);
        setAddons(p.addons || {});
        setForm((f) => ({ ...f, ...(p.form || {}) }));
      } catch {}
    }
  }, [editId]);

  useEffect(() => {
    if (prefilledRef.current) return;
    if (user && user.role === "customer" && !form.client_name && !form.client_email) {
      setForm((f) => ({ ...f, client_name: user.name || "", client_email: user.email || "" }));
    }
  }, [user]); // eslint-disable-line

  const { oneTime, monthly, lineItems } = useMemo(() => {
    let one = 0, mo = 0;
    const items = [];
    const selPkg = catalog.packages.find((p) => p.id === pkg);
    if (selPkg) { items.push({ name: `${selPkg.name} Package`, price: selPkg.price, billing: selPkg.billing }); one += selPkg.price; }
    catalog.addons.forEach((a) => {
      if (addons[a.id]) {
        items.push({ name: a.name, price: a.price, billing: a.billing });
        if (a.billing === "monthly") mo += a.price; else one += a.price;
      }
    });
    return { oneTime: one, monthly: mo, lineItems: items };
  }, [pkg, addons, catalog]);

  const hasSelection = lineItems.length > 0;

  const handleSubmit = async () => {
    if (!hasSelection) { toast.error("Select a package or at least one add-on"); return; }
    if (!form.client_name || !form.client_email) { toast.error("Add your name and email"); return; }

    if (!user) {
      if (!form.password || form.password.length < 8) { toast.error("Choose a password (8+ characters) to create your account"); return; }
      setSubmitting(true);
      const tId = toast.loading("Building your quote & creating your account…");
      try {
        const addon_ids = Object.keys(addons).filter((k) => addons[k]);
        const { data } = await api.post("/public/quotes", {
          client_name: form.client_name,
          client_email: form.client_email,
          company: form.company,
          package_id: pkg,
          addon_ids,
          needs: form.needs,
          website_url: hasWebsite ? form.website_url : "",
          website: form.website,
          password: form.password,
        });
        sessionStorage.removeItem("pendingQuote");
        toast.dismiss(tId);
        if (data.account_exists) {
          toast.message("You already have an account", { description: "Please log in to review and pay for your quote." });
          sessionStorage.setItem("postLoginRedirect", `/quotes/${data.id}`);
          navigate("/login");
          return;
        }
        await refresh();
        toast.success("Quote ready — review, sign, and pay your deposit.");
        navigate(`/quotes/${data.id}`);
      } catch (e) {
        toast.dismiss(tId);
        toast.error(e.response?.data?.detail || "Failed to submit your quote");
      } finally {
        setSubmitting(false);
      }
      return;
    }

    setSubmitting(true);
    try {
      const addon_ids = Object.keys(addons).filter((k) => addons[k]);
      const body = {
        client_name: form.client_name,
        client_email: form.client_email,
        company: form.company,
        package_id: pkg,
        addon_ids,
        needs: form.needs,
        website_url: hasWebsite ? form.website_url : "",
        from_audit: fromAudit,
      };
      const { data } = editId
        ? await api.put(`/quotes/${editId}`, body)
        : await api.post("/quotes", body);
      sessionStorage.removeItem("pendingQuote");
      toast.success(editId ? "Quote updated!" : "Quote created!");
      navigate(`/quotes/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save quote");
    } finally {
      setSubmitting(false);
    }
  };

  const grouped = useMemo(() => {
    const g = {};
    catalog.addons.forEach((a) => { (g[a.category] = g[a.category] || []).push(a); });
    return g;
  }, [catalog]);

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-20" data-testid="public-quote-success">
        <div className="border border-zinc-900 bg-white shadow-[8px_8px_0px_0px_rgba(10,10,10,1)] p-8 sm:p-12 text-center">
          <CheckCircle2 className="h-14 w-14 text-primary mx-auto" />
          <h1 className="mt-6 font-display font-black text-3xl sm:text-4xl tracking-tighter">Quote sent!</h1>
          <p className="mt-3 text-zinc-600">
            {submitted.proposal
              ? <>We've emailed your custom estimate <span className="font-medium text-zinc-900">and a tailored proposal</span> to <span className="font-medium text-zinc-900">{submitted.email}</span>. Our team will review your goals and follow up shortly.</>
              : <>We've emailed a copy of your estimate to <span className="font-medium text-zinc-900">{submitted.email}</span>. Our team will review your goals and follow up shortly.</>}
          </p>
          <div className="mt-8 bg-zinc-50 border border-zinc-200 p-5 text-left">
            <p className="font-display font-bold text-zinc-900">Want to track this quote?</p>
            <p className="text-sm text-zinc-600 mt-1">Create a free account to view your full proposal, e-sign, and pay your deposit online.</p>
          </div>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              onClick={() => navigate("/register")}
              data-testid="success-create-account-btn"
              className="rounded-none bg-primary hover:bg-[#5E1F92] text-white font-mono uppercase tracking-widest h-11 px-6"
            >
              Create Account
            </Button>
            <Button
              onClick={() => { setSubmitted(null); setPkg(null); setAddons({}); setHasWebsite(false); setForm({ client_name: "", client_email: "", company: "", needs: "", website_url: "", website: "" }); }}
              data-testid="success-new-quote-btn"
              variant="outline"
              className="rounded-none border-zinc-900 font-mono uppercase tracking-widest h-11 px-6"
            >
              Build Another
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="quote-builder-page">
      <div className="mb-8">
        <span className="font-mono text-xs uppercase tracking-[0.2em] text-primary">Quote Builder</span>
        <h1 className="mt-2 font-display font-black text-4xl lg:text-5xl tracking-tighter">{editId ? "Edit your quote" : "Build your quote"}</h1>
        <p className="mt-2 text-zinc-600">Pick a package, layer on add-ons, and watch your total update live.</p>
      </div>

      <div className="grid lg:grid-cols-12 gap-8 items-start">
        {/* Builder */}
        <div className="lg:col-span-8 space-y-10">
          {/* Step 1: Packages */}
          <section>
            <h2 className="font-mono text-sm uppercase tracking-widest text-zinc-500 mb-4">01 — Choose a package</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {catalog.packages.map((p) => {
                const active = pkg === p.id;
                return (
                  <button
                    key={p.id}
                    data-testid={`tier-${p.id}-select`}
                    onClick={() => setPkg(active ? null : p.id)}
                    className={`text-left p-5 border transition-all ${
                      active ? "border-primary ring-2 ring-primary bg-primary/5" : "border-zinc-300 bg-white hover:border-zinc-900"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <h3 className="font-display font-bold text-xl">{p.name}</h3>
                      {p.popular && <span className="font-mono text-[9px] uppercase bg-primary text-white px-1.5 py-0.5">Popular</span>}
                    </div>
                    <p className="font-display font-black text-3xl tracking-tighter mt-2">{fmt(p.price)}</p>
                    <p className="text-xs text-zinc-500 mt-1">{p.tagline}</p>
                    <ul className="mt-3 space-y-1">
                      {p.features.slice(0, 3).map((f) => (
                        <li key={f} className="flex items-start gap-1.5 text-xs text-zinc-600">
                          <Check className="h-3 w-3 text-primary mt-0.5 shrink-0" /> {f}
                        </li>
                      ))}
                    </ul>
                    {active && <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-primary">✓ Selected</p>}
                  </button>
                );
              })}
            </div>
          </section>

          {/* Step 2: Add-ons */}
          <section>
            <h2 className="font-mono text-sm uppercase tracking-widest text-zinc-500 mb-4">02 — Custom add-ons</h2>
            <div className="space-y-6">
              {Object.entries(grouped).map(([cat, items]) => (
                <div key={cat}>
                  <h3 className="font-display font-bold text-sm text-zinc-900 mb-2">{cat}</h3>
                  <div className="border border-zinc-200 bg-white divide-y divide-zinc-100">
                    {items.map((a) => (
                      <label
                        key={a.id}
                        className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-zinc-50"
                      >
                        <div>
                          <p className="font-medium text-sm">{a.name}</p>
                          <p className="font-mono text-xs text-zinc-500">
                            {fmt(a.price)} {a.billing === "monthly" ? "/mo" : "one-time"}
                          </p>
                          {a.features && a.features.length > 0 && (
                            <p className="text-xs text-zinc-400 mt-0.5">{a.features.join(" · ")}</p>
                          )}
                        </div>
                        <Switch
                          data-testid={`addon-${a.id}-toggle`}
                          checked={!!addons[a.id]}
                          onCheckedChange={(v) => setAddons((s) => ({ ...s, [a.id]: v }))}
                        />
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Step 3: Lead capture */}
          <section>
            <h2 className="font-mono text-sm uppercase tracking-widest text-zinc-500 mb-4">03 — Your details</h2>
            <div className="grid sm:grid-cols-2 gap-4 bg-white border border-zinc-200 p-5">
              <div>
                <Label className="font-mono text-xs uppercase tracking-widest">Name</Label>
                <Input data-testid="builder-name-input" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} className="mt-1.5 rounded-none border-zinc-300 focus-visible:ring-primary" placeholder="Jane Doe" />
              </div>
              <div>
                <Label className="font-mono text-xs uppercase tracking-widest">Email</Label>
                <Input data-testid="builder-email-input" type="email" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} className="mt-1.5 rounded-none border-zinc-300 focus-visible:ring-primary" placeholder="you@company.com" />
              </div>
              <div className="sm:col-span-2">
                <Label className="font-mono text-xs uppercase tracking-widest">Company (optional)</Label>
                <Input data-testid="builder-company-input" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="mt-1.5 rounded-none border-zinc-300 focus-visible:ring-primary" placeholder="Acme Inc." />
              </div>
              {!user && (
                <div className="sm:col-span-2">
                  <Label className="font-mono text-xs uppercase tracking-widest">Create a password</Label>
                  <Input data-testid="builder-password-input" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5 rounded-none border-zinc-300 focus-visible:ring-primary" placeholder="At least 8 characters" autoComplete="new-password" />
                  <p className="mt-1 font-mono text-[11px] text-zinc-400">We'll set up your account so you can review, sign, and pay right away.</p>
                </div>
              )}
              <div className="sm:col-span-2 border border-zinc-200 p-4 bg-zinc-50">
                <label className="flex items-center justify-between cursor-pointer">
                  <span className="font-mono text-xs uppercase tracking-widest text-zinc-700">Do you have an existing website?</span>
                  <Switch data-testid="has-website-toggle" checked={hasWebsite} onCheckedChange={setHasWebsite} />
                </label>
                {hasWebsite && (
                  <div className="mt-3">
                    <Input
                      data-testid="builder-website-url-input"
                      value={form.website_url}
                      onChange={(e) => setForm({ ...form, website_url: e.target.value })}
                      className="rounded-none border-zinc-300 focus-visible:ring-primary"
                      placeholder="https://yourcurrentsite.com"
                    />
                    <p className="mt-1.5 text-[11px] text-zinc-400">We'll review your current site to tailor the right recommendations.</p>
                  </div>
                )}
              </div>
              <div className="sm:col-span-2">
                <Label className="font-mono text-xs uppercase tracking-widest">Project goals (helps AI tailor your proposal)</Label>
                <Textarea data-testid="builder-needs-input" value={form.needs} onChange={(e) => setForm({ ...form, needs: e.target.value })} className="mt-1.5 rounded-none border-zinc-300 focus-visible:ring-primary min-h-[90px]" placeholder="e.g. We're launching a DTC skincare brand and need a Shopify site + SEO to rank for 'organic skincare'." />
              </div>
              <div className="sm:col-span-2" aria-hidden="true" style={{ position: "absolute", left: "-9999px", top: "-9999px", height: 0, width: 0, overflow: "hidden" }}>
                <Label>Website</Label>
                <Input tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="Your website" />
              </div>
            </div>
          </section>
        </div>

        {/* Sticky receipt */}
        <div className="lg:col-span-4 lg:sticky lg:top-24">
          <motion.div layout className="border border-zinc-900 bg-white shadow-[8px_8px_0px_0px_rgba(10,10,10,1)]">
            <div className="bg-zinc-900 text-white px-5 py-4 flex items-center gap-2">
              <Receipt className="h-5 w-5" />
              <h3 className="font-display font-bold text-lg">Live Quote</h3>
            </div>
            <div className="p-5">
              {!hasSelection ? (
                <p className="text-sm text-zinc-500 py-6 text-center">Select a package or add-ons to see your quote.</p>
              ) : (
                <ul className="space-y-2 mb-4" data-testid="receipt-line-items">
                  {lineItems.map((li, i) => (
                    <li key={i} className="flex items-start justify-between text-sm">
                      <span className="text-zinc-700 pr-2">{li.name}</span>
                      <span className="font-mono whitespace-nowrap">
                        {fmt(li.price)}{li.billing === "monthly" ? "/mo" : ""}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              <div className="border-t border-zinc-200 pt-4 space-y-2">
                <div className="flex justify-between items-baseline">
                  <span className="font-mono text-xs uppercase tracking-widest text-zinc-500">One-time</span>
                  <span data-testid="quote-live-total" className="font-display font-black text-2xl tracking-tighter">{fmt(oneTime)}</span>
                </div>
                {monthly > 0 && (
                  <div className="flex justify-between items-baseline">
                    <span className="font-mono text-xs uppercase tracking-widest text-zinc-500">Monthly</span>
                    <span className="font-display font-bold text-xl text-primary">{fmt(monthly)}/mo</span>
                  </div>
                )}
              </div>
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                data-testid="generate-quote-btn"
                className="w-full mt-5 rounded-none bg-primary hover:bg-[#5E1F92] text-white font-mono uppercase tracking-widest h-11"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : editId ? "Update Quote" : user ? "Create Quote" : "Get My Quote"}
              </Button>
              <p className="mt-3 text-[11px] text-zinc-400 text-center">No payment required to generate a quote.</p>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
